import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { post_earnings_visible } from '../api';
import { error_message } from '../format';
import {
  bulk_action_label,
  bulk_confirm_text,
  bulk_form_error,
  bulk_payload,
  bulk_result_text,
  bulk_result_type
} from './earnings_logic';

/*
 * PROD-7828 A-15 — Overview bulk bar: show or hide the teacher app's Earnings page for the selected teachers,
 * with a required reason (audit trail). One request, one transaction server-side; staff accounts come back
 * refused by name in the result toast. on_done(data) runs after a change landed (the caller reloads).
 */
const EarningsDialog = ({ visible, uuids, on_close, on_done, on_notify }) => {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [server_error, setServerError] = useState('');
  const error = bulk_form_error(uuids, reason);

  const submit = () => {
    setTouched(true);
    if (error || busy) return;
    setBusy(true);
    setServerError('');
    post_earnings_visible(bulk_payload(uuids, visible, reason))
      .then((data) => {
        setBusy(false);
        on_notify(bulk_result_type(data), bulk_result_text(data));
        on_done(data);
        on_close();
      })
      .catch((e) => {
        setBusy(false);
        setServerError(error_message(e, 'Earnings visibility was not changed. Try again.'));
      });
  };

  return (
    <TpDialog
      title={bulk_action_label(visible)}
      on_close={on_close}
      busy={busy}
      guard_text={discard_guard_text(reason)}
    >
      <div className="tp-modal">
        <div className="tp-ctx">{bulk_confirm_text(visible, uuids.length)}</div>
        <div className="tp-field">
          <label htmlFor="tp-earnings-reason">Reason (required, recorded in the audit trail)</label>
          <textarea
            id="tp-earnings-reason"
            className="tp-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="e.g. Earnings pilot, batch 1"
          />
          {touched && error ? (
            <span className="tp-neg" role="alert">
              {error}
            </span>
          ) : null}
          {server_error ? (
            <span className="tp-neg" role="alert">
              {server_error}
            </span>
          ) : null}
        </div>
        <div className="tp-modal-ft">
          {/* aria-disabled, not disabled: disabling the focused button drops focus (CD-114). */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={busy ? undefined : on_close}
            aria-disabled={busy ? 'true' : undefined}
          >
            Cancel
          </button>
          <button
            type="button"
            className={visible ? 'tp-btn' : 'tp-btn tp-btn-danger'}
            onClick={busy ? undefined : submit}
            aria-disabled={busy ? 'true' : undefined}
          >
            {busy ? 'Saving…' : bulk_action_label(visible)}
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

const EarningsBulkBar = ({ uuids, on_clear, on_done, on_notify }) => {
  const [action, setAction] = useState(null); // true = show, false = hide
  if (!uuids.length) return null;
  return (
    <div className="tp-frow" role="region" aria-label="Earnings bulk actions">
      <span className="tp-card-hd-sub">{uuids.length} selected</span>
      <button type="button" className="tp-btn-out" onClick={() => setAction(true)}>
        {bulk_action_label(true)}
      </button>
      <button type="button" className="tp-btn-out" onClick={() => setAction(false)}>
        {bulk_action_label(false)}
      </button>
      <button type="button" className="tp-btn-ghost" onClick={on_clear}>
        Clear selection
      </button>
      {action !== null ? (
        <EarningsDialog
          visible={action}
          uuids={uuids}
          on_close={() => setAction(null)}
          on_done={on_done}
          on_notify={on_notify}
        />
      ) : null}
    </div>
  );
};

export default EarningsBulkBar;
